(() => {
  "use strict";

  const config = window.HEIR || {};
  const strings = config.strings || {};
  const locale = typeof config.locale === "string" ? config.locale : "en";
  const isChinese = locale.toLowerCase().startsWith("zh");
  const designProducts = new WeakMap();
  let catalogueRequest;

  // Display-only retail approvals, never a source for checkout or availability.
  const approvedRetailPrices = Object.freeze({
    "bridge-zip": 198, "core-hoodie": 138, "core-tee": 68, "signature-tee": 78,
    "avator-jackets-flight-bomber": 198, "avator-jackets-minimal-ma-1": 178,
    "avator-jackets-varsity-jacket": 228, "avator-above-the-clouds-tee": 98,
    "avator-runway-tee": 98, "avator-runway-hoodie": 178
  });
  const approvedRetailPrice = (product) => {
    const retail = product && product.approvedRetailPrice;
    if (!retail || !Object.hasOwn(approvedRetailPrices, product.handle) ||
        [product.rightsReview, product.archiveOnly].some((flag) => flag === true || flag === "true") ||
        retail.currency !== "USD" || !Number.isFinite(retail.amount) ||
        retail.amount !== approvedRetailPrices[product.handle]) return "";
    return `US$${retail.amount.toFixed(2)}`;
  };
  const retailPriceNotice = () => isChinese
    ? "确认零售价 · 暂未开放下单"
    : "Approved retail price · Ordering not yet open";

  const text = (key, fallback) => {
    const value = strings[key];
    return typeof value === "string" && value.trim() ? value : fallback;
  };

  const normalized = (value) => String(value == null ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");

  const collectionSearchAliases = new Map([
    ["avator", "航空 飞行"],
    ["avator-jackets", "航空 外套"],
    ["graphic", "图案 印花"],
    ["core", "经典 核心"],
    ["seasonal", "季节"],
    ["cut-01", "首发"]
  ]);

  const appendPreviewLocale = (href) => {
    if (!config.localPreview || !isChinese) return href;
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set("lang", "zh-CN");
      if (url.origin === window.location.origin) {
        return `${url.pathname}${url.search}${url.hash}`;
      }
      return url.toString();
    } catch (_error) {
      const separator = href.includes("?") ? "&" : "?";
      return `${href}${separator}lang=zh-CN`;
    }
  };

  const storefrontUrl = (path) => {
    const cleanPath = String(path || "").replace(/^\/+/, "");
    const root = typeof config.rootUrl === "string" && config.rootUrl ? config.rootUrl : "/";
    const href = `${root.replace(/\/+$/, "")}/${cleanPath}`.replace(/([^:]\/)\/+/g, "$1");
    return appendPreviewLocale(href);
  };

  const assetUrl = (path) => {
    const value = String(path || "");
    if (/^(?:https?:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) return value;
    const base = typeof config.assetBase === "string" ? config.assetBase : "";
    return `${base.replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
  };

  const focusable = (container) => Array.from(container.querySelectorAll(
    "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
  )).find((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");

  const restoreFocus = (element) => {
    if (element && element.isConnected && typeof element.focus === "function") element.focus();
  };

  const isCatalogueHydrationGrid = (grid) => {
    const host = grid.closest("[data-catalogue-mode]");
    return !host || normalized(host.dataset.catalogueMode) !== "false";
  };

  const openDialog = (dialog, opener, preferredFocus) => {
    if (!dialog) return;
    dialog.__heirOpener = opener || document.activeElement;
    if (typeof dialog.showModal === "function") {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.hidden = false;
      dialog.setAttribute("open", "");
    }
    window.requestAnimationFrame(() => {
      const target = preferredFocus || dialog.querySelector("[autofocus]") || focusable(dialog) || dialog;
      if (!target.hasAttribute("tabindex") && target === dialog) target.setAttribute("tabindex", "-1");
      target.focus();
    });
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === "function" && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      dialog.hidden = true;
      restoreFocus(dialog.__heirOpener);
      dialog.__heirOpener = null;
    }
  };

  const bindDialog = (dialog) => {
    if (!dialog || dialog.dataset.heirBound === "true") return;
    dialog.dataset.heirBound = "true";
    dialog.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest("[data-dialog-close], [data-search-close], [data-zoom-close]");
      if (button && dialog.contains(button)) closeDialog(dialog);
    });
    dialog.addEventListener("close", () => {
      restoreFocus(dialog.__heirOpener);
      dialog.__heirOpener = null;
    });
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && typeof dialog.showModal !== "function") {
        event.preventDefault();
        closeDialog(dialog);
      }
    });
  };

  const ensureDesignDialog = () => {
    const existing = document.getElementById("design-dialog");
    if (existing) return existing;
    const dialog = document.createElement("dialog");
    const bar = document.createElement("div");
    const label = document.createElement("span");
    const close = document.createElement("button");
    const body = document.createElement("div");
    const gallery = document.createElement("div");
    const content = document.createElement("div");
    const title = document.createElement("h2");
    const meta = document.createElement("p");
    const price = document.createElement("p");
    const priceNote = document.createElement("p");
    const description = document.createElement("p");
    const visualizationNotice = document.createElement("p");
    const rightsNotice = document.createElement("p");
    const disclaimer = document.createElement("p");

    dialog.id = "design-dialog";
    dialog.className = "search-dialog design-dialog";
    dialog.setAttribute("aria-labelledby", "design-dialog-title");
    bar.className = "dialog-bar";
    label.dataset.designLabel = "";
    label.textContent = isChinese ? "产品详情" : "Product details";
    close.type = "button";
    close.dataset.dialogClose = "";
    close.setAttribute("aria-label", text("close", isChinese ? "关闭" : "Close"));
    close.textContent = "✕";
    body.className = "design-dialog__body";
    gallery.className = "design-dialog__gallery";
    gallery.dataset.designGallery = "";
    content.className = "design-dialog__content";
    title.id = "design-dialog-title";
    title.dataset.designTitle = "";
    meta.className = "design-dialog__meta";
    meta.dataset.designMeta = "";
    price.className = "product-details__price";
    price.dataset.designRetailPrice = "";
    price.hidden = true;
    priceNote.className = "design-dialog__meta";
    priceNote.dataset.designRetailNotice = "";
    priceNote.hidden = true;
    description.dataset.designDescription = "";
    visualizationNotice.className = "design-dialog__meta";
    visualizationNotice.dataset.designVisualizationNotice = "";
    rightsNotice.className = "design-dialog__rights-notice";
    rightsNotice.dataset.designRightsNotice = "";
    rightsNotice.hidden = true;
    rightsNotice.setAttribute("role", "note");
    disclaimer.className = "design-dialog__disclaimer";
    disclaimer.dataset.designDisclaimer = "";
    bar.append(label, close);
    content.append(title, meta, price, priceNote, description, visualizationNotice, rightsNotice, disclaimer);
    body.append(gallery, content);
    dialog.append(bar, body);
    document.body.append(dialog);
    return dialog;
  };

  const showDesignProduct = (dialog, product, trigger) => {
    const title = localizedTitle(product);
    const description = isChinese && product.descriptionZh
      ? String(product.descriptionZh)
      : String(product.description || product.descriptionZh || "");
    const gallery = dialog.querySelector("[data-design-gallery]");
    const titleNode = dialog.querySelector("[data-design-title]");
    const meta = dialog.querySelector("[data-design-meta]");
    const price = dialog.querySelector("[data-design-retail-price]");
    const priceNote = dialog.querySelector("[data-design-retail-notice]");
    const retail = approvedRetailPrice(product);
    const descriptionNode = dialog.querySelector("[data-design-description]");
    const visualizationNotice = dialog.querySelector("[data-design-visualization-notice]");
    const rightsNotice = dialog.querySelector("[data-design-rights-notice]");
    const disclaimer = dialog.querySelector("[data-design-disclaimer]");
    if (gallery) {
      const sourceGallery = Array.isArray(product.gallery) && product.gallery.length
        ? product.gallery
        : [{ image: product.image, alt: title, altZh: title }];
      const images = sourceGallery
        .filter((entry) => entry && typeof entry === "object" && entry.image)
        .map((entry, index) => {
          const figure = document.createElement("figure");
          const image = document.createElement("img");
          figure.className = "design-dialog__gallery-item";
          image.className = "design-dialog__gallery-image";
          image.src = assetUrl(entry.image);
          image.alt = String((isChinese && entry.altZh) || entry.alt || entry.altZh || title);
          image.loading = index === 0 ? "eager" : "lazy";
          image.decoding = "async";
          figure.append(image);
          return figure;
        });
      gallery.replaceChildren(...images);
    }
    if (titleNode) titleNode.textContent = title;
    if (meta) meta.textContent = [product.number, localizedColor(product)].filter(Boolean).map(String).join(" · ");
    if (price) {
      price.hidden = !retail;
      price.textContent = retail;
    }
    if (priceNote) {
      priceNote.hidden = !retail;
      priceNote.textContent = retail ? retailPriceNotice() : "";
    }
    if (descriptionNode) descriptionNode.textContent = description;
    if (visualizationNotice) visualizationNotice.textContent = isChinese ? "产品效果展示" : "Product visualization";
    if (rightsNotice) {
      const showRights = product.rightsReview === true || product.archiveOnly === true;
      rightsNotice.hidden = !showRights;
      rightsNotice.textContent = showRights
        ? isChinese
          ? `${text("rightsReview", "权益审核中")}：在相关图像、艺术作品及商标权利完成书面确认前，仅可作为内部存档与设计参考，不得发布、销售或投入生产。`
          : `${text("rightsReview", "Rights review required")}: Internal archive and design reference only. Do not publish, sell or produce until the relevant image, artwork and trademark rights are confirmed in writing.`
        : "";
    }
    if (disclaimer) {
      disclaimer.textContent = isChinese
        ? "暂未开放下单。请查看商品信息与发售更新。"
        : "Ordering is not yet open. Explore the product details and release updates.";
    }
    let actions = dialog.querySelector('[data-design-growth-actions]');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'design-growth-actions';
      actions.dataset.designGrowthActions = '';
      disclaimer?.after(actions);
    }
    actions.replaceChildren();
    if (!product.archiveOnly) {
      const updates = document.createElement('a');
      updates.className = 'text-link';
      updates.href = storefrontUrl(`pages/drop-room?design=${encodeURIComponent(product.id)}`);
      updates.textContent = isChinese ? '查看首发预约' : 'Explore Drop Room';
      const fit = document.createElement('a');
      fit.className = 'text-link';
      fit.href = storefrontUrl(`pages/fit-guide?style=${encodeURIComponent(product.id)}`);
      fit.textContent = isChinese ? '版型与尺码' : 'Find your fit';
      actions.append(createSaveControl(product), updates, fit);
    }
    openDialog(dialog, trigger, dialog.querySelector("[data-dialog-close]"));
    window.HEIRInsights?.track?.('view', product.id);
  };

  const initMobileMenu = () => {
    const menu = document.getElementById("mobile-menu");
    const toggles = Array.from(document.querySelectorAll("[data-menu-toggle]"));
    if (!menu || !toggles.length) return;
    let opener = null;

    const setOpen = (open, returnFocus) => {
      menu.hidden = !open;
      toggles.forEach((toggle) => toggle.setAttribute("aria-expanded", String(open)));
      if (open) {
        window.requestAnimationFrame(() => {
          const target = focusable(menu);
          if (target) target.focus();
        });
      } else if (returnFocus) {
        restoreFocus(opener);
      }
      if (!open) opener = null;
    };

    setOpen(false, false);
    toggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const willOpen = menu.hidden;
        if (willOpen) opener = toggle;
        setOpen(willOpen, !willOpen);
      });
    });
    menu.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest("a[href], [data-menu-close]")) setOpen(false, false);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !menu.hidden) {
        event.preventDefault();
        setOpen(false, true);
      }
    });
  };

  const initDialogs = () => {
    const searchDialog = document.getElementById("search-dialog");
    const zoomDialog = document.getElementById("zoom-dialog");
    const hasDesignGrid = Array.from(document.querySelectorAll("[data-catalogue-grid]")).some(isCatalogueHydrationGrid);
    const designDialog = !config.localPreview && hasDesignGrid ? ensureDesignDialog() : null;
    bindDialog(searchDialog);
    bindDialog(zoomDialog);
    bindDialog(designDialog);

    document.querySelectorAll("[data-search-open]").forEach((trigger) => {
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.addEventListener("click", () => {
        openDialog(searchDialog, trigger, searchDialog && searchDialog.querySelector("input[type='search']"));
      });
    });

    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest("[data-zoom]");
      if (!trigger || !zoomDialog) return;
      const sourceImage = trigger.matches("img") ? trigger : trigger.querySelector("img");
      const source = trigger.dataset.src || trigger.dataset.zoomSrc || (sourceImage && (sourceImage.currentSrc || sourceImage.src));
      if (!source) return;
      event.preventDefault();
      const image = zoomDialog.querySelector("[data-zoom-image]") || zoomDialog.querySelector("img");
      if (image) {
        image.src = source;
        image.alt = trigger.dataset.alt || (sourceImage && sourceImage.alt) || text("viewDesign", "Expanded product design");
      }
      openDialog(zoomDialog, trigger, zoomDialog.querySelector("[data-dialog-close], [data-zoom-close]"));
    });

    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest("[data-design-open]");
      if (!trigger || !designDialog) return;
      const product = designProducts.get(trigger);
      if (!product) return;
      event.preventDefault();
      showDesignProduct(designDialog, product, trigger);
    });

    document.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('[data-heir-open-design]');
      if (!trigger) return;
      event.preventDefault();
      try {
        const products = await loadCatalogue();
        const product = products.find((item) => item.archiveOnly !== true && item.id === trigger.dataset.heirOpenDesign);
        if (!product) throw new Error('Design unavailable');
        const dialog = ensureDesignDialog();
        bindDialog(dialog);
        showDesignProduct(dialog, product, trigger);
      } catch {
        if (trigger.href) window.location.assign(trigger.href);
      }
    });

    if (zoomDialog) {
      zoomDialog.addEventListener("close", () => {
        const image = zoomDialog.querySelector("[data-zoom-image]") || zoomDialog.querySelector("img");
        if (image) image.removeAttribute("src");
      });
    }
  };

  const initProductGallery = () => {
    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const selection = event.target.closest("[data-gallery-select]");
      if (!selection) return;
      const scope = selection.closest(".product-page, [data-product-gallery]") || document;
      const primary = scope.querySelector(".product-image__zoom");
      const primaryImage = primary && (primary.querySelector(".product-image__media") || primary.querySelector("img"));
      const imageSource = selection.dataset.imageSrc || "";
      const zoomSource = selection.dataset.src || imageSource;
      const alt = selection.dataset.alt || "";
      if (!primary || !primaryImage || !imageSource) return;
      event.preventDefault();
      primaryImage.src = imageSource;
      primaryImage.removeAttribute("srcset");
      primaryImage.alt = alt;
      primary.dataset.src = zoomSource;
      primary.dataset.alt = alt;
      scope.querySelectorAll(".product-actions__design").forEach((action) => {
        action.dataset.src = zoomSource;
        action.dataset.alt = alt;
      });
      scope.querySelectorAll("[data-gallery-select]").forEach((button) => {
        const selected = button === selection;
        button.setAttribute("aria-pressed", String(selected));
        button.classList.toggle("is-active", selected);
      });
    });
  };

  const loadCatalogue = () => {
    if (!catalogueRequest) {
      const url = typeof config.catalogueUrl === "string" ? config.catalogueUrl : "";
      catalogueRequest = url
        ? fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
          .then((response) => {
            if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);
            return response.json();
          })
          .then((payload) => {
            const products = Array.isArray(payload) ? payload : payload && Array.isArray(payload.products) ? payload.products : null;
            if (!products) throw new Error("Catalogue response is not a product array");
            return products;
          })
        : Promise.reject(new Error("Catalogue URL is unavailable"));
    }
    return catalogueRequest;
  };

  const localizedTitle = (product) => {
    if (isChinese && product.titleZh) return String(product.titleZh);
    return String(product.title || product.titleZh || product.number || "HEIR");
  };

  const localizedColor = (product) => {
    if (isChinese && product.colorZh) return String(product.colorZh);
    return String(product.color || product.colorZh || "");
  };

  const createSaveControl = (product) => {
    const button = document.createElement('button');
    const label = document.createElement('span');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    button.type = 'button';
    button.className = 'heir-save-button';
    button.dataset.heirSaveDesign = String(product.id || product.handle || '');
    button.dataset.saveLabel = isChinese ? '收藏' : 'Save to The Edit';
    button.dataset.savedLabel = isChinese ? '已收藏' : 'Saved';
    button.setAttribute('aria-pressed', 'false');
    button.disabled = true;
    label.dataset.heirSaveLabel = '';
    label.textContent = button.dataset.saveLabel;
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    path.setAttribute('d', 'M5 3h10v14l-5-3-5 3z');
    svg.append(path);
    button.append(svg, label);
    return button;
  };

  const createProductCard = (product) => {
    const wrapper = document.createElement('article');
    const card = document.createElement("a");
    const imageFrame = document.createElement("div");
    const image = document.createElement("img");
    const info = document.createElement("div");
    const title = document.createElement("h3");
    const color = document.createElement("span");
    const mark = document.createElement("b");
    const notice = product.archiveOnly === true ? document.createElement("p") : null;
    const handle = String(product.handle || product.id || "");
    const productTitle = localizedTitle(product);

    wrapper.className = 'product-card';
    card.className = 'product-card__link';
    if (config.localPreview) {
      card.href = storefrontUrl(`products/${encodeURIComponent(handle)}`);
    } else {
      card.href = `#design-${encodeURIComponent(String(product.id || handle))}`;
      card.dataset.designOpen = "";
      card.setAttribute("aria-haspopup", "dialog");
      const rightsLabel = product.archiveOnly === true ? ` · ${text("rightsReview", isChinese ? "权益审核中" : "Rights review required")}` : "";
      card.setAttribute("aria-label", `${text("viewDesign", isChinese ? "查看设计" : "View design")}: ${productTitle}${rightsLabel}`);
      designProducts.set(card, product);
    }
    imageFrame.className = "product-card__image";
    image.src = assetUrl(product.image);
    image.alt = productTitle;
    image.loading = "lazy";
    image.decoding = "async";
    info.className = "product-card__info";
    title.textContent = productTitle;
    color.textContent = localizedColor(product);
    if (notice) {
      notice.className = "product-card__notice";
      notice.textContent = text("rightsReview", isChinese ? "权益审核中" : "Rights review required");
    }
    mark.textContent = "+";
    mark.setAttribute("aria-hidden", "true");
    imageFrame.append(image);
    info.append(title, color);
    const retail = approvedRetailPrice(product);
    if (retail) {
      const price = document.createElement("p");
      const priceNote = document.createElement("span");
      price.className = "product-card__price";
      price.dataset.catalogueRetailPrice = "";
      price.textContent = retail;
      priceNote.dataset.catalogueRetailNotice = "";
      priceNote.textContent = retailPriceNotice();
      info.append(price, priceNote);
    }
    if (notice) info.append(notice);
    info.append(mark);
    card.append(imageFrame, info);
    wrapper.append(card);
    if (!product.archiveOnly) wrapper.append(createSaveControl(product));
    return wrapper;
  };

  const productMatchesFeaturedKey = (product, key) => {
    const token = normalized(key);
    const values = [product.id, product.handle, product.number].map(normalized);
    return values.includes(token) || values.some((value) => value.startsWith(`${token}-`));
  };

  const searchMatches = (product, rawQuery) => {
    const query = String(rawQuery || "").slice(0, 256).trim().toLowerCase();
    if (!query) return true;
    const collectionAliases = collectionSearchAliases.get(normalized(product.collection)) || "";
    const haystack = [
      product.id, product.handle, product.number, product.title, product.titleZh, product.color, product.colorZh,
      product.typeEn, product.typeZh,
      product.category, product.collection, collectionAliases, product.description, product.descriptionZh,
      product.detailsZh, product.fitEn, product.fitZh
    ].map((value) => String(value == null ? "" : value).toLowerCase()).join(" ");
    return query.split(/\s+/).slice(0, 16).every((term) => haystack.includes(term));
  };

  const sortProducts = (products, value) => {
    const mode = normalized(value || "featured");
    const items = products.slice();
    const byNumber = (a, b) => {
      const first = Number.parseInt(a.number, 10);
      const second = Number.parseInt(b.number, 10);
      return (Number.isFinite(first) ? first : Number.MAX_SAFE_INTEGER) - (Number.isFinite(second) ? second : Number.MAX_SAFE_INTEGER);
    };
    const byTitle = (a, b) => localizedTitle(a).localeCompare(localizedTitle(b), locale, { sensitivity: "base" });
    if (["number-asc", "oldest", "ascending"].includes(mode)) return items.sort(byNumber);
    if (["number-desc", "newest", "descending"].includes(mode)) return items.sort((a, b) => byNumber(b, a));
    if (["title-asc", "name-asc", "name-ascending"].includes(mode)) return items.sort(byTitle);
    if (["title-desc", "name-desc", "name-descending"].includes(mode)) return items.sort((a, b) => byTitle(b, a));
    return items;
  };

  const initCatalogueGrid = (grid) => {
    const scope = grid.closest("[data-catalogue-section]") || grid.closest(".collection-page, .search-page, section") || grid.parentElement || document;
    const categoryControls = Array.from(scope.querySelectorAll("[data-category]"));
    const sortControl = scope.querySelector("[data-sort]");
    const countNodes = Array.from(scope.querySelectorAll("[data-result-count], [data-catalogue-count]"));
    const emptyNodes = Array.from(scope.querySelectorAll("[data-empty-state]"));
    let products = [];
    let activeCategory = "all";

    const selectedControl = categoryControls.find((control) => control.getAttribute("aria-pressed") === "true" || control.classList.contains("is-active"));
    if (selectedControl) activeCategory = normalized(selectedControl.dataset.category || selectedControl.value || selectedControl.textContent);

    const updateCategoryControls = () => {
      categoryControls.forEach((control) => {
        const selected = normalized(control.dataset.category || control.value || control.textContent) === activeCategory;
        control.setAttribute("aria-pressed", String(selected));
        control.classList.toggle("is-active", selected);
      });
    };

    const baseSelection = () => {
      let selected = products.slice();
      if (grid.dataset.featured) {
        const keys = grid.dataset.featured.split(",").map((key) => key.trim()).filter(Boolean);
        selected = keys.map((key) => products.find((product) => productMatchesFeaturedKey(product, key))).filter(Boolean);
      } else {
        const scopedCollection = grid.dataset.collection || (scope.dataset && scope.dataset.collectionHandle) || "all";
        const collection = normalized(scopedCollection) || "all";
        if (collection !== "all") {
          selected = selected.filter((product) => normalized(product.collection) === collection);
        }
      }
      if (grid.hasAttribute("data-search-query")) {
        selected = selected.filter((product) => searchMatches(product, grid.dataset.searchQuery));
      }
      return selected;
    };

    const render = () => {
      let selected = baseSelection();
      if (activeCategory !== "all") {
        selected = selected.filter((product) => normalized(product.category) === activeCategory);
      }
      selected = sortProducts(selected, sortControl && sortControl.value);
      grid.replaceChildren(...selected.map(createProductCard));
      countNodes.forEach((node) => { node.textContent = String(selected.length); });
      emptyNodes.forEach((node) => { node.hidden = selected.length !== 0; });
      grid.setAttribute("aria-busy", "false");
    };

    categoryControls.forEach((control) => {
      control.addEventListener("click", (event) => {
        event.preventDefault();
        activeCategory = normalized(control.dataset.category || control.value || control.textContent) || "all";
        updateCategoryControls();
        render();
      });
    });
    if (sortControl) sortControl.addEventListener("change", render);
    updateCategoryControls();
    grid.setAttribute("aria-busy", "true");

    loadCatalogue().then((loadedProducts) => {
      products = loadedProducts.filter((product) => product && typeof product === "object");
      if (!config.localPreview) products = products.filter((product) => product.archiveOnly !== true);
      render();
    }).catch(() => {
      grid.replaceChildren();
      grid.setAttribute("aria-busy", "false");
      countNodes.forEach((node) => { node.textContent = "0"; });
      emptyNodes.forEach((node) => { node.hidden = true; });
      let error = scope.querySelector("[data-catalogue-error]");
      if (!error) {
        error = document.createElement("p");
        error.dataset.catalogueError = "";
        error.className = "catalogue-error";
        error.setAttribute("role", "alert");
        grid.insertAdjacentElement("afterend", error);
      }
      error.textContent = text("error", isChinese ? "产品目录暂时无法载入，请稍后再试。" : "The catalogue could not be loaded. Please try again.");
      error.hidden = false;
    });
  };

  const initCatalogue = () => {
    document.querySelectorAll("[data-catalogue-grid]").forEach((grid) => {
      if (isCatalogueHydrationGrid(grid)) initCatalogueGrid(grid);
    });
  };

  const initPreviewForms = () => {
    document.querySelectorAll('form.language-switch').forEach((form) => {
      form.addEventListener('submit', () => {
        const target = form.querySelector('input[name="return_to"]');
        if (target) target.value = window.location.pathname + window.location.search;
      });
    });
    document.querySelectorAll("form[data-preview-form]").forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        form.reset();
        const status = form.querySelector("[data-form-status]");
        if (status) {
          status.textContent = text("previewNotice", isChinese ? "这是预览表单；信息未发送，也未保存。" : "This is a preview form. Your information was not sent or stored.");
          status.hidden = false;
        }
      });
    });
  };

  const init = () => {
    [initMobileMenu, initDialogs, initProductGallery, initCatalogue, initPreviewForms].forEach((feature) => {
      try {
        feature();
      } catch (error) {
        window.console.error("HEIR storefront feature failed to initialize", error);
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
